import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {UseMainLayout} from "../../../context/LayoutContext";
import {ResponseInterface} from "../../../interfaces/ResponseInterface";
import {config} from "../../../config";
import SetupApi from "../../../common/api/functions/Setup";
import LogV2Api from "../../../common/api/functions/v2/Log";
import IntegrateV2Api from "../../../common/api/functions/v2/Integrate";
import DashboardV2Api from "../../../common/api/functions/v2/Dashboard";
import QModal from "../../../common/components/QModal";
import {RouteDefine} from "../../../routes/RouteDefine";
import {SetupInterface} from "../../../interfaces/private/SetupInterface";
import {LogInterface, structLog} from "../../../interfaces/private/LogInterface";
import {
    getIncompleteEnabledSections,
    getSchedulableSyncModules,
    isSetupSectionComplete,
    isSyncModuleEnabled,
    shouldShowCommonSetup,
    usesCustomerBalanceLedger,
} from "../../../common/helpers/setupCompleteness";
import {getMissingSetupKeys, isCheckSetupComplete, SetupLinkKey} from "../../../common/helpers/checkSetup";
import {SyncModuleKey} from "../../public/SetupV2/helpers/onboardingModules";
import {setToast} from "../../../app/rootReducer";
import SettingsPageHeader from "../Settings/components/SettingsPageHeader";
import DashboardHealthStrip from "./components/DashboardHealthStrip";
import DashboardModuleCard, {
    DashboardIssueCounts,
    DashboardModuleKey,
} from "./components/DashboardModuleCard";
import DashboardScheduleSnippet from "./components/DashboardScheduleSnippet";
import DashboardRecentActivity from "./components/DashboardRecentActivity";
import BodyModalIntegrationComponent from "../LogV2/components/BodyModalIntegrationComponent";
import BodyModalMessageSetupComponent from "../LogV2/components/BodyModalMessageSetupComponent";
import BodyModalLogDetailComponent from "../LogV2/components/BodyModalLogDetailComponent";

interface ServiceState {
    name: string;
    status: string;
    last_run: string;
    count_message: number;
}

const MODULE_DEFS: Array<{
    key: DashboardModuleKey;
    titleKey: string;
    logRoute: string;
}> = [
    {key: "invoice", titleKey: "common.navbar.invoices", logRoute: RouteDefine.invoice},
    {key: "refund", titleKey: "common.navbar.refunds", logRoute: RouteDefine.refund},
    {key: "payment", titleKey: "common.navbar.payments", logRoute: RouteDefine.payment},
    {key: "payout", titleKey: "common.navbar.payouts", logRoute: RouteDefine.payout},
    {key: "subscription", titleKey: "common.navbar.subscriptions", logRoute: RouteDefine.subscription},
];

const emptyIssues = (): Record<DashboardModuleKey, DashboardIssueCounts> => ({
    invoice: {error: 0, notice: 0, total: 0},
    refund: {error: 0, notice: 0, total: 0},
    payment: {error: 0, notice: 0, total: 0},
    payout: {error: 0, notice: 0, total: 0},
    subscription: {error: 0, notice: 0, total: 0},
});

const isModuleSetupComplete = (setup: SetupInterface, module: SyncModuleKey): boolean => {
    const sectionOk = isSetupSectionComplete(module, setup);
    if (module === "invoice" || module === "refund") {
        return sectionOk
            && (!shouldShowCommonSetup(setup) || isSetupSectionComplete("common", setup));
    }
    return sectionOk;
};

const DashboardV2 = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    UseMainLayout();

    const [loading, setLoading] = useState(true);
    const [setup, setSetup] = useState<SetupInterface>({});
    const [services, setServices] = useState<ServiceState[]>([]);
    const [issueCounts, setIssueCounts] = useState(emptyIssues);
    const [recentLogs, setRecentLogs] = useState<LogInterface[]>([]);
    const [scheduleActiveCount, setScheduleActiveCount] = useState(0);
    const [scheduleTotalCount, setScheduleTotalCount] = useState(0);
    const [scheduledTypes, setScheduledTypes] = useState<string[]>([]);

    const [syncModule, setSyncModule] = useState<DashboardModuleKey | null>(null);
    const [syncChecking, setSyncChecking] = useState(false);
    const [progress, setProgress] = useState(0);
    const [openMessageSetup, setOpenMessageSetup] = useState(false);
    const [openModalIntegration, setOpenModalIntegration] = useState(false);
    const [missingSetup, setMissingSetup] = useState<SetupLinkKey[]>([]);
    const [openLogDetail, setOpenLogDetail] = useState(false);
    const [logDetail, setLogDetail] = useState<LogInterface>(structLog);
    const [loadingLogDetail, setLoadingLogDetail] = useState(false);

    const refreshData = useCallback(async () => {
        setLoading(true);
        try {
            const [setupRes, dashboardRes] = await Promise.all([
                SetupApi.detail(),
                DashboardV2Api.summary(),
            ]);

            if ((setupRes as ResponseInterface)?.status === config.STATUS_CODE.OK) {
                setSetup((setupRes as ResponseInterface)?.data?.data || {});
            }

            if ((dashboardRes as ResponseInterface)?.status === config.STATUS_CODE.OK) {
                const data = (dashboardRes as ResponseInterface)?.data?.data || {};
                setServices(data.services || []);
                setRecentLogs(data.recent || []);

                const nextIssues = emptyIssues();
                const rawCounts = data.issue_counts || {};
                MODULE_DEFS.forEach((module) => {
                    const row = rawCounts[module.key] || {};
                    nextIssues[module.key] = {
                        error: Number(row.error) || 0,
                        notice: Number(row.notice) || 0,
                        total: Number(row.total) || ((Number(row.error) || 0) + (Number(row.notice) || 0)),
                    };
                });
                setIssueCounts(nextIssues);

                const schedules = data.schedules || {};
                setScheduleActiveCount(Number(schedules.active) || 0);
                setScheduleTotalCount(Number(schedules.total) || 0);
                setScheduledTypes(Array.isArray(schedules.types) ? schedules.types : []);
            }
        } catch {
            // ignore partial failures
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const stripeConnected = Boolean(setup.stripe_access_token);
    const economicConnected = Boolean(setup.economic_token);
    const incompleteSections = getIncompleteEnabledSections(setup);
    const firstIncompleteSection = incompleteSections[0] ?? null;

    const serviceByName = useMemo(() => {
        const map: Record<string, ServiceState> = {};
        services.forEach((s) => {
            map[s.name] = s;
        });
        return map;
    }, [services]);

    const enabledModules = useMemo(
        () => MODULE_DEFS.filter((module) => isSyncModuleEnabled(setup, module.key)),
        [setup],
    );

    /** Cards + schedule health: enabled, not ledger-skipped, setup complete. */
    const readyModules = useMemo(() => {
        const schedulable = new Set(getSchedulableSyncModules(setup));
        return enabledModules.filter(
            (module) =>
                schedulable.has(module.key)
                && isModuleSetupComplete(setup, module.key),
        );
    }, [enabledModules, setup]);

    const hiddenModuleCount = MODULE_DEFS.length - readyModules.length;

    const showWelcome = !loading
        && !stripeConnected
        && !economicConnected
        && services.every((s) => !s.last_run);

    const callIntegration = async (params: Record<string, unknown>) => {
        if (!syncModule) {
            return;
        }

        setProgress(1);
        try {
            let response: ResponseInterface | any;
            switch (syncModule) {
                case "invoice":
                    response = await IntegrateV2Api.syncInvoice(params);
                    break;
                case "payment":
                    response = await IntegrateV2Api.syncPayment(params);
                    break;
                case "payout":
                    response = await IntegrateV2Api.syncPayout(params);
                    break;
                case "subscription":
                    response = await IntegrateV2Api.syncSubscription(params);
                    break;
                case "refund":
                    response = await IntegrateV2Api.syncRefund(params);
                    break;
            }
            setProgress(100);
            await refreshData();
            return response;
        } catch {
            setProgress(0);
            dispatch(setToast({message: t("log.sync_failed"), type: config.TOAST_TYPE.ERROR}));
        }
    };

    const handleRunSync = async (moduleKey: DashboardModuleKey) => {
        if (syncChecking || (progress > 0 && progress < 100)) {
            return;
        }
        if (usesCustomerBalanceLedger(setup) && moduleKey === "invoice") {
            dispatch(setToast({
                message: t("economic.accountingModeInvoiceSkippedHelp"),
                type: config.TOAST_TYPE.ERROR,
            }));
            return;
        }

        setSyncModule(moduleKey);
        setSyncChecking(true);
        try {
            const response: ResponseInterface | any = await SetupApi.checkSetup(moduleKey);
            if (response?.data?.data) {
                const data = response.data.data;
                if (isCheckSetupComplete(data)) {
                    setOpenModalIntegration(true);
                } else {
                    setMissingSetup(getMissingSetupKeys(data));
                    setOpenMessageSetup(true);
                }
            }
        } catch {
            dispatch(setToast({message: t("log.setup_check_failed"), type: config.TOAST_TYPE.ERROR}));
        } finally {
            setSyncChecking(false);
        }
    };

    const handleIntegrationClosed = () => {
        setProgress(0);
        setSyncModule(null);
    };

    const handleViewLogDetail = async (id: string | number) => {
        setLoadingLogDetail(true);
        setOpenLogDetail(true);
        try {
            const response: ResponseInterface | any = await LogV2Api.getDetail(id);
            if (response?.status === config.STATUS_CODE.OK) {
                setLogDetail(response?.data?.data || structLog);
            } else {
                setLogDetail(structLog);
            }
        } catch {
            setLogDetail(structLog);
        } finally {
            setLoadingLogDetail(false);
        }
    };

    const visibleRecentLogs = recentLogs.filter((log) =>
        isSyncModuleEnabled(setup, log.type as DashboardModuleKey),
    );

    return (
        <React.Fragment>
            <QModal
                open={openMessageSetup}
                setOpen={setOpenMessageSetup}
                headerContent={t("log.setup_notice_title")}
                bodyContent={(
                    <BodyModalMessageSetupComponent missing={missingSetup}/>
                )}
            />

            <QModal
                open={openModalIntegration}
                setOpen={setOpenModalIntegration}
                headerContent={t("common.modal.integration")}
                bodyContent={(
                    <BodyModalIntegrationComponent
                        setOpen={setOpenModalIntegration}
                        open={openModalIntegration}
                        callIntegration={callIntegration}
                        progress={progress}
                        showForceImport={syncModule === "invoice"}
                    />
                )}
                onClosed={handleIntegrationClosed}
            />

            <QModal
                size="xl"
                open={openLogDetail}
                setOpen={(open: boolean) => {
                    setOpenLogDetail(open);
                    if (!open) {
                        setLogDetail(structLog);
                    }
                }}
                headerContent={t("log.detail")}
                onClosed={() => setLogDetail(structLog)}
                bodyContent={(
                    <BodyModalLogDetailComponent
                        setOpen={setOpenLogDetail}
                        open={openLogDetail}
                        setLoading={setLoadingLogDetail}
                        loading={loadingLogDetail}
                        log={logDetail}
                    />
                )}
            />

            <div className="row">
                <SettingsPageHeader
                    title={t("common.dashboard.title")}
                    subtitle={t("common.dashboard.subtitle")}
                    actions={(
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Link to={RouteDefine.help} className="btn btn-sm btn-light">
                                {t("common.navbar.help")}
                            </Link>
                            <button
                                type="button"
                                className="btn btn-sm btn-light"
                                disabled={loading}
                                onClick={() => refreshData()}
                                title={t("common.dashboard.refresh")}
                            >
                                <i className={`las ${loading ? "la-spinner la-spin" : "la-sync"} me-1`}/>
                                {t("common.dashboard.refresh")}
                            </button>
                        </div>
                    )}
                />

                {showWelcome ? (
                    <div className="col-12 mb-3">
                        <div className="alert alert-info mb-0">
                            <strong>{t("common.dashboard.welcomeTitle")}</strong>
                            <div className="small mt-1">{t("common.dashboard.welcomeHelp")}</div>
                        </div>
                    </div>
                ) : null}

                <DashboardHealthStrip
                    stripeConnected={stripeConnected}
                    economicConnected={economicConnected}
                    incompleteSetupCount={incompleteSections.length}
                    firstIncompleteSection={firstIncompleteSection}
                    compact={showWelcome}
                    ledgerMode={usesCustomerBalanceLedger(setup)}
                />

                <div className="col-12 mb-2 d-flex align-items-end justify-content-between gap-2 flex-wrap">
                    <div>
                        <h3 className="fs-18 fw-bold mb-0">{t("common.dashboard.modulesTitle")}</h3>
                        <p className="text-muted small mb-0">{t("common.dashboard.modulesHelp")}</p>
                    </div>
                    {hiddenModuleCount > 0 ? (
                        <p className="text-muted small mb-0">
                            {t("common.dashboard.hiddenNotReadyModules", {
                                count: hiddenModuleCount,
                            })}
                        </p>
                    ) : null}
                </div>

                <div className="col-12 mb-3">
                    {loading ? (
                        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-5 g-3 placeholder-glow">
                            {Array.from({length: Math.max(readyModules.length, 1)}).map((_, i) => (
                                <div className="col" key={i}>
                                    <div className="business-detail bg-orange-bland-zp bdr-20 pd-25" style={{minHeight: 160}}>
                                        <span className="placeholder col-7 mb-3 d-block"/>
                                        <span className="placeholder col-5 mb-2 d-block"/>
                                        <span className="placeholder col-9 d-block"/>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : readyModules.length === 0 ? (
                        <div className="alert alert-warning mb-0">
                            {enabledModules.length === 0
                                ? t("common.dashboard.noEnabledModules")
                                : t("common.dashboard.noReadyModules")}
                        </div>
                    ) : (
                        <div className={`row row-cols-1 row-cols-md-2 g-3 ${
                            readyModules.length >= 5
                                ? "row-cols-xl-5"
                                : readyModules.length === 4
                                    ? "row-cols-xl-4"
                                    : readyModules.length === 3
                                        ? "row-cols-xl-3"
                                        : "row-cols-xl-2"
                        }`}>
                            {readyModules.map((module) => {
                                const isSyncingThis = syncChecking && syncModule === module.key;
                                const runSyncDisabled = !stripeConnected
                                    || !economicConnected
                                    || (progress > 0 && progress < 100);

                                return (
                                    <DashboardModuleCard
                                        key={module.key}
                                        moduleKey={module.key}
                                        title={t(module.titleKey)}
                                        logRoute={module.logRoute}
                                        syncEnabled
                                        setupComplete
                                        lastRun={serviceByName[module.key]?.last_run}
                                        issueCounts={issueCounts[module.key]}
                                        showRunSync
                                        onRunSync={() => handleRunSync(module.key)}
                                        runSyncLoading={isSyncingThis}
                                        runSyncDisabled={runSyncDisabled}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                <DashboardRecentActivity
                    logs={visibleRecentLogs}
                    modules={enabledModules}
                    loading={loading}
                    onView={handleViewLogDetail}
                />

                <DashboardScheduleSnippet
                    activeCount={scheduleActiveCount}
                    totalCount={scheduleTotalCount}
                    scheduledTypes={scheduledTypes}
                    enabledModules={readyModules}
                    loading={loading}
                />
            </div>
        </React.Fragment>
    );
};

export default React.memo(DashboardV2);
